import { lazy, Suspense } from 'react'
import { CustomRoutes, Resource } from 'react-admin'
import { Navigate, Outlet, Route } from 'react-router-dom'
import ProtectedRoute from '@component/Common/ProtectedRoute'
import { BuildingScope, LegacyBuildingRedirect, LegacyPortfolioRedirect, PortfolioScope } from '@/layout/BuildingScope'
import AnalyticsCategoryHeader from '@/analytics/AnalyticsCategoryHeader'
const Configuration = lazy(() => import('./configuration/Configuration'))
const AccountDetail = lazy(() => import('./account/AccountDetail'))
const PropertyAdd = lazy(() => import('./account/PropertyAdd'))
const BuildingInfoSection = lazy(() => import('./account/BuildingInfoSection'))
const AccountInfoSection = lazy(() => import('./account/AccountInfoSection'))
const AdminSearch = lazy(() => import('./dashboard/AdminSearch'))
const Overall = lazy(() => import('./analytics/feedback/Overall'))
const GlobalReport = lazy(() => import('./analytics/feedback/GlobalReport'))
const ScanReport = lazy(() => import('./analytics/scan'))
const SubscriptionReport = lazy(() => import('./analytics/report/SubscriptionReport'))
const ImportQRCode = lazy(() => import('./qrcodes/ImportQRCode'))
const PropertyQrGenerator = lazy(() => import('./qrcodes/PropertyQrGenerator'))
const ReportBathroomService = lazy(() => import('./analytics/feedback/BathroomService'))
const RoundsEngine = lazy(() => import('@pages/roundsEngine'))
const RoundsEngineBuilder = lazy(() => import('@pages/roundsEngine/builder'))
const ServiceRequestReport = lazy(() => import('@analytics/tenant/ServiceRequestReport'))
const CustomerFeedbackReport = lazy(() => import('@analytics/feedback/CustomerFeedbackReport'))
const Reset = lazy(() => import('@/layout/Reset'))
const CustomerPortal = lazy(() => import('./qrcodes/CustomerPortal'))
const Notifications = lazy(() => import('@/pages/notifications/Notifications'))
const EmailLog = lazy(() => import('@/pages/notifications/EmailLog'))
const ContentSurvey = lazy(() => import('@/pages/survey/ContentSurvey'))
const PrintProof = lazy(() => import('@pages/print/PrintProof'))
const TwoQREdit = lazy(() => import('@pages/twoQRLibrary/TwoQREdit'))
const InAppFeedbackReport = lazy(() => import('@pages/dashboardReport/InAppFeedbackReport'))
const DataIntegrityDashboard = lazy(() => import('@pages/dashboardReport/DataIntegrityDashboard'))
const PMLibraryDashboard = lazy(() => import('@pages/dashboardReport/PMLibraryDashboard'))
const SignProof = lazy(() => import('@pages/digitalProof/SignProof'))
const SurveyList = lazy(() => import('@pages/survey/SurveyList'))
const CollectHome = lazy(() => import('@pages/collect'))
const PcaHubHome = lazy(() => import('@pages/pcaHub'))
const CollectSurveyPage = lazy(() => import('@pages/collect/public/CollectSurveyPage'))
const ComplianceRecordsBuilding = lazy(() => import('@pages/dashboardReport/ComplianceRecords/ComplianceRecordsBuilding'))
const GlobalComplianceRecords = lazy(() => import('@pages/dashboardReport/ComplianceRecords/GlobalComplianceRecords'))
const SurveyBuilder = lazy(() => import('@pages/survey/SurveyBuilder'))
const MasterNotepad = lazy(() => import('@pages/dashboardReport/MasterNotepad'))
const StaffCheckIn = lazy(() => import('@pages/dashboardReport/StaffCheckIn'))
const UpsellMatrixReport = lazy(() => import('@pages/dashboardReport/UpsellMatrixReport'))
const DataArchive = lazy(() => import('@pages/DataArchive'))
const PdfGoogleReviewReport = lazy(() => import('@pages/pdf/PdfGoogleReviewReport'))
const PropertyCharacteristicsIndex = lazy(() => import('@pages/dashboardReport/PropertyCharacteristicsIndex'))
const ResearchTest = lazy(() => import('@pages/ResearchTest'))
const EquipmentTrend = lazy(() => import('@pages/dashboardReport/EquipmentTrend'))
const PortfolioRoundAnalytics = lazy(() => import('@analytics/round/PortfolioRoundAnalytics'))
const JobReport = lazy(() => import('@pages/dashboardReport/JobReport'))
const SystemJobs = lazy(() => import('@pages/systemJobs/SystemJobs'))
const Telemetry = lazy(() => import('@pages/telemetry/Telemetry'))
const ResidentFeedback = lazy(() => import('@pages/dashboardReport/ResidentFeedback'))
const Video = lazy(() => import('@pages/Video'))
const InsuranceDashboard = lazy(() => import('@pages/dashboardReport/InsuranceDashboard'))
const AccountingDashboard = lazy(() => import('@pages/AccountingDashboard'))
const TenantContacts = lazy(() => import('@pages/tenant/TenantContacts'))
import { Login } from '@/layout'
const QRCodeEdit = lazy(() => import('./qrcodes/QRCodeEdit'))
const PdfBuilderSolution = lazy(() => import('@pages/pdf/PdfBuilderSolution'))
const PdfSocialReputation = lazy(() => import('@pages/pdf/PdfSocialReputation'))
const PdfProofReport = lazy(() => import('@pages/pdf/PdfProofReport'))
const MenuTabs = lazy(() => import('@pages/MenuTabs'))
const SupplierCheckInReport = lazy(() => import('@pages/SupplierCheckInReport'))
const Feedback = lazy(() => import('./analytics/feedback'))
// Building-scoped Inspection Compliance (the cadence ops matrix). The legacy
// building component (@analytics/inspection/InspectionReport) still backs the
// react-admin /inspectionReport Resource in App.tsx until launch cleanup.
const InspectionComplianceBuilding = lazy(() => import('@pages/dashboardReport/InspectionComplianceBuilding'))
const TenantExperience = lazy(() => import('@analytics/tenant/TenantExperience'))
const SupplierSLAReport = lazy(() => import('@analytics/risk/SupplierSLAReport'))
const RoundAnalytics = lazy(() => import('@analytics/round/RoundAnalytics'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const AccountCreate = lazy(() => import('./account/AccountCreate'))
const Reports = lazy(() => import('@/pages/Reports'))
const IntelligencePortal = lazy(() => import('@/pages/IntelligencePortal'))
const CustomerActivity = lazy(() => import('@/pages/CustomerActivity'))
const TicketMonitor = lazy(() => import('@pages/ticketMonitor/TicketMonitor'))
const VerifySurvey = lazy(() => import('@/pages/survey/VerifySurvey'))
const ContentCollection = lazy(() => import('@pages/survey/ContentCollection'))
const SurveyWelcome = lazy(() => import('@pages/survey/SurveyWelcom'))
const DigitalProofWelcome = lazy(() => import('@pages/digitalProof/DigitalProofWelcome'))
const DigitalProofCarousel = lazy(() => import('@pages/digitalProof/DigitalProofCarousel'))
const UpdateCOI = lazy(() => import('@pages/coi/updateCOI'))
const ReviewCOI = lazy(() => import('@pages/coi/reviewCOI'))
const CollectionReport = lazy(() => import('@pages/pm/CollectionReport'))
const CollectionReportSubmit = lazy(() => import('@pages/pm/CollectionReportSubmit'))
const BathroomServiceDashboard = lazy(() => import('@pages/dashboardReport/BathroomServiceDashboard'))
const CleaningIQPortfolio = lazy(() => import('@/pages/dashboardReport/CleaningIQPortfolio'))
const CleaningIQ = lazy(() => import('@pages/dashboardReport/CleaningIQ'))
const EngineeringIQPortfolio = lazy(() => import('@/pages/dashboardReport/EngineeringIQPortfolio'))
const EngineeringIQ = lazy(() => import('@pages/dashboardReport/EngineeringIQ'))
const SecurityIQPortfolio = lazy(() => import('@/pages/dashboardReport/SecurityIQPortfolio'))
const SecurityIQ = lazy(() => import('@pages/dashboardReport/SecurityIQ'))
const TeamPerformance = lazy(() => import('@pages/dashboardReport/TeamPerformance'))
const GlobalTeamPerformance = lazy(() => import('@pages/dashboardReport/GlobalTeamPerformance'))
const PreventativeMaintenance = lazy(() => import('@pages/dashboardReport/PreventativeMaintenance'))
const PdfRoundAnalytics = lazy(() => import('@pages/dashboardReport/PdfRoundAnalytics'))
const PdfWorkOrderIntelligence = lazy(
  () => import('@pages/dashboardReport/workOrderIntelligence/PdfWorkOrderIntelligence')
)
const PdfTenantFeedback = lazy(() => import('@pages/dashboardReport/tenantFeedback/PdfTenantFeedback'))
const PdfPMIntelligence = lazy(() => import('@pages/dashboardReport/pmIntelligence/PdfPMIntelligence'))
const PinActivity = lazy(() => import('@analytics/scan/PinActivity'))
const EquipmentLookup = lazy(() => import('@pages/dashboardReport/EquipmentLookup'))
const EquipmentDashboard = lazy(() => import('@pages/dashboardReport/EquipmentDashboard'))
const GlobalEquipmentDashboard = lazy(() => import('@pages/dashboardReport/GlobalEquipmentDashboard'))
const PdfRoundDetail = lazy(() => import('@pages/dashboardReport/PdfRoundDetail'))
const PdfTeamPerformance = lazy(() => import('@pages/pdf/PdfTeamPerformance'))
const GoogleRatingReport = lazy(() => import('@analytics/report/GoogleRatingReport'))
const PdfIncidentReport = lazy(() => import('@pages/dashboardReport/PdfIncidentReport'))
const ReportBuilder = lazy(() => import('@pages/pdf/ReportBuilder'))
const PdfReportBuilder = lazy(() => import('@pages/pdf/PdfReportBuilder'))
const PdfWeeklyException = lazy(() => import('@pages/pdf/PdfWeeklyException'))
const PdfLegal = lazy(() => import('@pages/pdf/PdfLegal'))
const PdfCharacteristics = lazy(() => import('@pages/pdf/PdfCharacteristics'))
const ResidentLog = lazy(() => import('@pages/dashboardReport/ResidentLog'))
const GuestLog = lazy(() => import('@pages/dashboardReport/GuestLog'))
const PdfMechanicalSystem = lazy(() => import('@pages/pdf/PdfMechanicalSystem'))
const PdfChiefEngineer = lazy(() => import('@pages/pdf/PdfChiefEngineer'))
const PdfWorkLetter = lazy(() => import('@pages/pdf/PdfWorkLetter'))
const PdfMobileEmployee = lazy(() => import('@pages/pdf/PdfMobileEmployee'))
const PortfolioMobileEmployee = lazy(() => import('@pages/dashboardReport/PortfolioMobileEmployee'))
const PdfInstallationGuide = lazy(() => import('@pages/pdf/PdfInstallationGuide'))
const PdfDetailInvoice = lazy(() => import('@pages/pdf/PdfDetailInvoice'))
const Migration = lazy(() => import('@pages/Migration'))
const ImpakMigration = lazy(() => import('@pages/ImpakMigration'))
const CustomFeedback = lazy(() => import('@pages/dashboardReport/CustomFeedback'))
const CustomFeedbackDetail = lazy(() => import('@pages/dashboardReport/CustomFeedbackDetail'))
const MultipleContentSurvey = lazy(() => import('@pages/survey/MultipleContentSurvey'))
const ComprehensiveFeedback = lazy(() => import('@pages/dashboardReport/ComprehensiveFeedback'))
const WorkOrderDashboard = lazy(() => import('@pages/dashboardReport/WorkOrderDashboard'))
const MasterNotepadDashboard = lazy(() => import('@pages/dashboardReport/MasterNotepadDashboard'))
const CollectionDashboard = lazy(() => import('@pages/dashboardReport/CollectionDashboard'))
const PortfolioCollectionDashboard = lazy(() => import('@pages/dashboardReport/PortfolioCollectionDashboard'))
const PdfBuildingInvoice = lazy(() => import('@pages/pdf/PdfBuildingInvoice'))
const PdfGroupInvoice = lazy(() => import('@pages/pdf/PdfGroupInvoice'))
const PdfTenantInvoice = lazy(() => import('@pages/pdf/PdfTenantInvoice'))
const Calendar = lazy(() => import('@pages/Calendar'))
const VisitorLog = lazy(() => import('@pages/VisitorLog'))
const DeficiencyTracker = lazy(() => import('@pages/dashboardReport/DeficiencyTracker'))
const DeficiencyTrackerGlobal = lazy(() => import('@pages/dashboardReport/DeficiencyTrackerGlobal'))
const DeficiencyDetail = lazy(() => import('@pages/dashboardReport/DeficiencyDetail'))
const WorkOrderIntelligenceDashboard = lazy(() => import('@pages/dashboardReport/WorkOrderIntelligenceDashboard'))
const CustomerFeedbackOnSRDashboard = lazy(() =>
  import('@pages/pdf/PdfFeedbackOnSR').then((module) => ({ default: module.CustomerFeedbackOnSRDashboard }))
)
const PdfFeedbackOnSR = lazy(() => import('@pages/pdf/PdfFeedbackOnSR'))

const QRCodeList = lazy(() => import('./qrcodes/myqrcode/QRCodeList'))
const TwoQRLibrary = lazy(() => import('@pages/twoQRLibrary/TwoQRLibrary'))
const InspectionLibrary = lazy(() => import('@pages/inspectionLibrary'))
// Portfolio Inspection Overview — the cadence ops matrix that replaced the old
// building x inspection-type grid (analytics/feedback/InspectionReport, deleted).
const InspectionReportGlobal = lazy(() => import('@pages/dashboardReport/inspectionCompliance/InspectionComplianceDashboard'))
const GlobalInsuranceDashboard = lazy(() => import('@pages/dashboardReport/GlobalInsuranceDashboard'))
const LeaseAbstraction = lazy(() => import('@pages/dashboardReport/LeaseAbstraction'))
const GlobalLeaseAbstraction = lazy(() => import('@pages/dashboardReport/GlobalLeaseAbstraction'))
const RoofAbstraction = lazy(() => import('@pages/dashboardReport/RoofAbstraction'))
const GlobalRoofAbstraction = lazy(() => import('@pages/dashboardReport/RoofAbstraction/GlobalRoofAbstraction'))
const PortfolioDataIntegrityDashboard = lazy(
  () => import('@pages/dashboardReport/PortfolioDataIntegrityDashboard')
)
const InstallationRound = lazy(() => import('@pages/analytics/InstallationRound'))
const TenantLibrary = lazy(() => import('@pages/tenant/TenantLibrary'))
const ResidentsLibrary = lazy(() => import('@pages/residents/ResidentsLibrary'))
const QrEngineeringDashboard = lazy(() => import('@pages/qrEngineering/QrEngineeringDashboard'))
const PMLibrary = lazy(() => import('@pages/pm/PMLibrary'))
const EditPMLibrary = lazy(() => import('@pages/pm/EditPMLibrary'))
const VacancyLead = lazy(() => import('@pages/dashboardReport/VacancyLead'))
const LeadCapture = lazy(() => import('@pages/dashboardReport/LeadCapture'))
const SubLeadCapture = lazy(() => import('@pages/dashboardReport/SubLeadCapture'))
const MechanicalSpaceLibrary = lazy(() => import('@pages/MechanicalSpaceLibrary'))
const RenewalReport = lazy(() => import('@pages/dashboardReport/RenewalReport'))
const SubscriptionList = lazy(() => import('@pages/settings/SubscriptionList'))
const ContactListReport = lazy(() => import('@/account/ContactList'))
const PortfolioTeam = lazy(() => import('@/account/ContactList').then((module) => ({ default: module.PortfolioTeam })))
const VendorList = lazy(() => import('@/account/VendorList'))
const EquipmentLibrary = lazy(() => import('@pages/roundsEngine/equipment/EquipmentLibrary'))
const EngineeringRoundsList = lazy(() => import('@pages/roundsEngine/table/EngineeringRoundsTable'))
const MoveOutReport = lazy(() => import('@pages/dashboardReport/MoveOutReport'))
const PrintLibrary = lazy(() => import('@pages/print/PrintLibrary'))
const PrintHubLibrary = lazy(() => import('@pages/print/PrintHubLibrary'))
const PrintQRLibrary = lazy(() => import('@pages/print/PrintQRLibrary'))
const ReportBuilderSubscriptions = lazy(() => import('@pages/pdf/ReportBuilderSubscriptions'))
const Legal = lazy(() => import('@pages/Legal'))
const FeedbackEngine = lazy(() => import('@pages/settings/FeedbackEngine'))
const ProgramLibrary = lazy(() => import('@pages/print/ProgramLibrary'))
const PromptEngineering = lazy(() => import('@pages/settings/PromptEngineering'))
const Lookbook = lazy(() => import('@pages/settings/Lookbook'))
const ActivateNotepad = lazy(() => import('@pages/dashboardReport/ActivateNotepad'))
const ModelHubLibrary = lazy(() => import('@pages/print/ModelHubLibrary'))
const NotificationEngine = lazy(() => import('@pages/notificationEngine/NotificationEngine'))
const CapitalPlanning = lazy(() => import('@pages/dashboardReport/CapitalPlanning'))
const GlobalCapitalPlanning = lazy(() => import('@pages/dashboardReport/GlobalCapitalPlanning'))
const GlobalPMCompliance = lazy(() => import('@pages/dashboardReport/GlobalPMCompliance'))
const PMComplianceBuilding = lazy(() => import('@pages/dashboardReport/PMComplianceBuilding'))
const UserImpersonation = lazy(() => import('@pages/impersonation/UserImpersonation'))
// DEV-ONLY (3191 IQ cache viewer)
const EquipmentIQCache = lazy(() => import('@pages/dev/EquipmentIQCache'))
const ImpersonateRedeem = lazy(() => import('@pages/impersonation/ImpersonateRedeem'))
const RolesList = lazy(() => import('@pages/admin/roles/RolesList'))
const RolesEdit = lazy(() => import('@pages/admin/roles/RolesEdit'))
const FeatureFlagsPage = lazy(() => import('@pages/admin/featureFlags/FeatureFlagsPage'))
const SystemSettingsPage = lazy(() => import('@pages/admin/systemSettings/SystemSettingsPage'))

const Router = [
  <CustomRoutes key="/configuration">
    <Route path="/configuration" element={<Configuration />} />
  </CustomRoutes>,
  <CustomRoutes noLayout key="/adminSearch">
    <Route
      path="/adminSearch"
      element={
        <Suspense fallback="">
          <AdminSearch />
        </Suspense>
      }
    />
  </CustomRoutes>,
  <CustomRoutes noLayout key="/login">
    <Route path="/login/:loginKey?" element={<Login />} />
  </CustomRoutes>,
  <CustomRoutes key="/accountDetail">
    <Route path="/accountDetail" element={<AccountDetail />} />
  </CustomRoutes>,
  // <CustomRoutes key="/qrcode/:groupId/:bId/:qrId">
  //   <Route path="/qrcode/:groupId/:bId/:qrId?" element={<QRCodeEdit />} />
  // </CustomRoutes>,
  <CustomRoutes key="/feedbackOverall">
    <Route path="/feedbackOverall/:bId/:et?" element={<Overall />} />
  </CustomRoutes>,
  <CustomRoutes key="/globalReport">
    <Route path="/globalReport" element={<GlobalReport />} />
  </CustomRoutes>,
  <CustomRoutes key="/reportGlobalReport">
    <Route path="/reportGlobalReport" element={<GlobalReport />} />
  </CustomRoutes>,
  <CustomRoutes key="/importQRCode">
    <Route path="/importQRCode" element={<ImportQRCode />} />
  </CustomRoutes>,
  <CustomRoutes key="/propertyQrGenerator">
    <Route path="/propertyQrGenerator/:fromQr?" element={<PropertyQrGenerator />} />
  </CustomRoutes>,
  <CustomRoutes noLayout key="/report">
    <Route
      path="/report/:type/:rId"
      element={
        <Suspense fallback="">
          <SubscriptionReport />
        </Suspense>
      }
    />
  </CustomRoutes>,
  // Create mode only — editing an existing building lives at /b/:bId/buildingInfo.
  <CustomRoutes key="/propertyAdd">
    <Route
      path="/propertyAdd"
      element={
        <ProtectedRoute permissions={['ManagePropertyInfo']}>
          <PropertyAdd />
        </ProtectedRoute>
      }
    />
  </CustomRoutes>,
  <CustomRoutes key="/propertyAddLegacy">
    <Route path="/propertyAdd/:bId/:idx?" element={<LegacyBuildingRedirect to="/buildingInfo/:idx?" />} />
  </CustomRoutes>,
  <CustomRoutes key="/reportBathroomService">
    <Route path="/reportBathroomService/:bId/:eId?" element={<ReportBathroomService />} />
  </CustomRoutes>,
  <CustomRoutes key="/roundsEngineBuilder">
    <Route path="/roundsEngineBuilder/:type?/:id?" element={<RoundsEngineBuilder />} />
  </CustomRoutes>,
  <CustomRoutes key="/serviceRequestPortfolio">
    <Route path="/serviceRequestPortfolio" element={<ServiceRequestReport />} />
  </CustomRoutes>,
  <CustomRoutes key="/activateReport">
    <Route path="/activateReport" element={<CustomerFeedbackReport />} />
  </CustomRoutes>,
  <CustomRoutes noLayout key="/reset">
    <Route
      path="/reset"
      element={
        <Suspense fallback="">
          <Reset />
        </Suspense>
      }
    />
  </CustomRoutes>,
  <CustomRoutes key="/customerPortal">
    <Route path="/customerPortal" element={<CustomerPortal />} />
  </CustomRoutes>,
  <CustomRoutes key="/contentSurvey">
    <Route path="/contentSurvey" element={<ContentSurvey />} />
  </CustomRoutes>,
  <CustomRoutes key="/contentSurvey">
    <Route path="/contentSurvey/:gId/:bId/:builderId?" element={<ContentSurvey />} />
  </CustomRoutes>,
  <CustomRoutes noLayout key="/impersonate/redeem">
    <Route
      path="/impersonate/redeem"
      element={
        <Suspense fallback="">
          <ImpersonateRedeem />
        </Suspense>
      }
    />
  </CustomRoutes>,
]

// Portfolio dashboards that live outside /portfolioAnalytics but belong to it
// in the nav. Mounted under /p/:gId; the pre-scope paths are shims below.
const PORTFOLIO_DASHBOARD_ROUTES = {
  workOrder: {
    path: 'workOrderDashboardGlobal',
    element: (
      <>
        <AnalyticsCategoryHeader standalone hideTitle portfolio />
        <WorkOrderDashboard isGlobal />
      </>
    ),
    permissions: ['WorkOrderDashboard'],
  },
  accounting: {
    path: 'accountingDashboardGlobal',
    element: (
      <>
        <AnalyticsCategoryHeader standalone hideTitle portfolio />
        <AccountingDashboard isGlobal />
      </>
    ),
  },
  collection: {
    disabled: true,
    path: 'collectionDashboardGlobal',
    element: (
      <>
        <AnalyticsCategoryHeader standalone hideTitle portfolio />
        <PortfolioCollectionDashboard />
      </>
    ),
  },
  deficiency: {
    path: 'deficiencyTrackerGlobal',
    element: (
      <>
        <AnalyticsCategoryHeader standalone hideTitle portfolio />
        <DeficiencyTrackerGlobal />
      </>
    ),
  },
}

export const lazyRouter: any[] = [
  {
    // Building scope: every building-section URL carries the building id, so
    // links are shareable and back/forward restore the full context. Children
    // are relative; BuildingScope syncs redux from :bId and remounts on switch.
    path: '/b/:bId',
    element: <BuildingScope />,
    child: [
      {
        // Section shell (header + group tabs); BuildingInfoSection also
        // normalizes numeric legacy idx segments to the owning group slug.
        path: 'buildingInfo',
        element: <BuildingInfoSection />,
        permissions: ['ManagePropertyInfo'],
        child: [{ path: ':group/:section?', element: <PropertyAdd /> }],
      },
      {
        path: 'marketing',
        element: <MenuTabs type="marketing" key="marketing" />,
        permissions: ['ManageMarketing'],
        child: [{ path: 'customerPortal', element: <CustomerPortal /> }],
      },
      {
        path: 'contacts',
        element: <MenuTabs type="contacts" key="contacts" />,
        permissions: ['ManageContacts'],
        child: [
          { path: 'contactList', element: <ContactListReport /> },
          { path: 'tenantLibrary', element: <TenantLibrary /> },
          { path: 'tenantContacts', element: <TenantContacts /> },
          { path: 'residentsLibrary', element: <ResidentsLibrary /> },
          { path: 'vendorList', element: <VendorList /> },
        ],
      },
      {
        path: 'library',
        element: <MenuTabs type="library" key="library" />,
        child: [
          { path: 'qrcodelist', element: <QRCodeList /> },
          { path: 'twoQRLibrary', element: <TwoQRLibrary /> },
          { path: 'inspectionLibrary', element: <InspectionLibrary /> },
          { path: 'roundsEngine/:type?', element: <RoundsEngine /> },
          { path: 'equipmentLibrary', element: <EquipmentLibrary sType="Equipment" /> },
          {
            path: 'equipmentLocationLibrary',
            element: <EquipmentLibrary sType="Location" key="equipmentLocationLibrary" />,
          },
          { path: 'mechanicalSpaceLibrary', element: <MechanicalSpaceLibrary /> },
          { path: 'pmLibrary', element: <PMLibrary /> },
          { path: 'activatePMLibrary', element: <EngineeringRoundsList isPm /> },
        ],
      },
      {
        path: 'calendar',
        element: <Calendar />,
        permissions: ['Calendar'],
      },
      {
        // Passthrough guard only — the category/report tabs render inside the
        // dashboard shell (pages/Dashboard.tsx); Alerts, Reports, and
        // Subscriptions live as standalone scoped routes below, so the old
        // 4-tab MenuTabs shell is gone. Old tab paths redirect.
        path: 'analytics',
        element: <Outlet />,
        permissions: ['Analytics'],
        child: [
          { path: 'reports', element: <LegacyBuildingRedirect to="/reports" /> },
          { path: 'notifications', element: <LegacyBuildingRedirect to="/alerts" /> },
          { path: 'subscription', element: <LegacyBuildingRedirect to="/subscriptions" /> },
          {
            path: 'dashboard',
            element: <Dashboard key="/dashboard" />,
            permissions: ['AnalyticsDashboards'],
            // Per-report guards mirror the permissionKey that gates each entry
            // in the Dashboard dropdown, so a direct URL can no longer render a
            // report the picker hides. Param-driven reports (roundAnalytics,
            // tenantExperience, leadCapture) accept any of their variants'
            // keys; per-variant enforcement stays with the page itself.
            child: [
              {
                path: 'roundAnalytics/:roundType?',
                element: <RoundAnalytics />,
                permissions: [
                  'EngineeringRounds',
                  'AEDRounds',
                  'FireExtinguisherRounds',
                  'InspectionRounds',
                  'CleaningRounds',
                  'EquipmentDetailsCollectionRound',
                  'SecurityPatrols',
                ],
              },
              // Asset/COI dashboards moved under their nav items' tab shells;
              // old links keep resolving.
              { path: 'capitalPlanning', element: <LegacyBuildingRedirect to="/asset/capitalPlanning" /> },
              { path: 'leaseAbstractionDashboard', element: <LegacyBuildingRedirect to="/asset/lease" /> },
              { path: 'roofAbstraction', element: <LegacyBuildingRedirect to="/asset/roof" /> },
              { path: 'insuranceDashboard', element: <LegacyBuildingRedirect to="/coi/insuranceDashboard" /> },
              { path: 'supplierSLAReport', element: <SupplierSLAReport />, permissions: ['SupplierSLAReport'] },
              {
                path: 'tenantExperience/:typeIndex?',
                element: <TenantExperience />,
                permissions: ['TenantPerformanceDashboard', 'TenantHealthDashboard'],
              },
              { path: 'inspectionReport', element: <InspectionComplianceBuilding />, permissions: ['InspectionDashboard'] },
              { path: 'complianceRecords', element: <LegacyBuildingRedirect to="/asset/compliance" /> },
              { path: 'reportAnalyticsFeedback/:type/:bId?', element: <Feedback />, permissions: ['FeedbackDashboard'] },
              {
                path: 'supplierCheckInReport',
                element: <SupplierCheckInReport />,
                permissions: ['SupplierCheckInReport'],
              },
              {
                disabled: true,
                path: 'roofAccessAuthorizationReport',
                element: <SupplierCheckInReport type="RoofAccess" />,
                permissions: ['RoofAccessAuthorizationReport'],
              },
              {
                disabled: true,
                path: 'tenantHVACReport',
                element: <SupplierCheckInReport type="TenantHVAC" />,
                permissions: ['TenantHVACAuthorizationReport'],
              },
              { disabled: true, path: 'vacancyLead', element: <VacancyLead />, permissions: ['VacancyLead'] },
              {
                disabled: true,
                path: 'leadCapture/:type',
                element: <LeadCapture />,
                permissions: ['LeadCapture', 'EVRequestReport', 'RoomRental', 'FieldMarketing', 'RegistrantList'],
              },
              { disabled: true, path: 'renewalReport', element: <RenewalReport />, permissions: ['RenewalReport'] },
              { disabled: true, path: 'inAppFeedbackReport', element: <InAppFeedbackReport />, permissions: ['InAppFeedback'] },
              {
                path: 'dataIntegrityDashboard',
                element: <DataIntegrityDashboard />,
                permissions: ['EquipmentDataIntegrityDashboard'],
              },
              { path: 'bathroomService', element: <BathroomServiceDashboard />, permissions: ['BathroomService'] },
              {
                path: 'cleaningIQ/:bId?',
                element: <CleaningIQ />,
                permissions: ['CleaningRounds'],
                features: ['CleaningIntelligence'],
              },
              {
                path: 'engineeringIQ/:bId?',
                element: <EngineeringIQ />,
                permissions: ['EngineeringRounds'],
                features: ['EngineeringIntelligence'],
              },
              {
                path: 'securityIQ/:bId?',
                element: <SecurityIQ />,
                permissions: ['SecurityPatrols'],
                features: ['SecurityIntelligence'],
              },
              { path: 'teamPerformance', element: <TeamPerformance />, permissions: ['TeamPerformance'] },
              {
                path: 'preventativeMaintenance',
                element: <PreventativeMaintenance />,
                permissions: ['PreventativeMaintenance'],
                features: ['PMIntelligence'],
              },
              {
                path: 'pMLibraryDashboard',
                element: <PMLibraryDashboard />,
                permissions: ['PMLibraryDashboard'],
                features: ['PMIntelligence'],
              },
              {
                path: 'pmCompliance',
                element: <PMComplianceBuilding />,
                permissions: ['PreventativeMaintenance'],
                features: ['PMIntelligence'],
              },
              { disabled: true, path: 'moveOutReport', element: <MoveOutReport />, permissions: ['MoveOutInspection'] },
              {
                path: 'equipmentDashboard',
                element: <EquipmentDashboard />,
                permissions: ['EquipmentLookup'],
                features: ['EquipmentIntelligence'],
              },
              {
                path: 'equipmentLookup',
                element: <EquipmentLookup />,
                permissions: ['EquipmentLookup'],
                features: ['EquipmentIntelligence'],
              },
              {
                path: 'equipmentTrend',
                element: <EquipmentTrend />,
                permissions: ['EquipmentTrend'],
                features: ['EquipmentIntelligence'],
              },
              { path: 'masterNotepad', element: <MasterNotepad />, permissions: ['MasterNotepad'] },
              { disabled: true, path: 'staffCheckIn', element: <StaffCheckIn />, permissions: ['StaffCheckInReport'] },
              { disabled: true, path: 'residentLog', element: <ResidentLog />, permissions: ['ResidentLog'] },
              { disabled: true, path: 'guestLog', element: <GuestLog />, permissions: ['GuestLog'] },
              {
                disabled: true,
                path: 'residentFeedback',
                element: <ResidentFeedback isBuilding />,
                permissions: ['SocialReputationReport'],
              },
              {
                disabled: true,
                path: 'customFeedbackDashboard',
                element: <CustomFeedback />,
                permissions: ['CustomFeedbackDashboard'],
              },
              {
                disabled: true,
                path: 'comprehensiveFeedback',
                element: <ComprehensiveFeedback />,
                permissions: ['ComprehensiveFeedbackReport'],
              },
              { disabled: true, path: 'customerFeedbackOnSR', element: <CustomerFeedbackOnSRDashboard /> },
            ],
          },
        ],
      },
      {
        // One nav item, four tabs (nav reorg): the intelligence dashboards
        // plus Mapping Hub, which renders through the buildingInfo shell
        // aliased to the same tab list. The dashboards keep the analytics
        // permission pair they had at their old /analytics/dashboard routes.
        path: 'asset',
        element: <MenuTabs type="asset" key="asset" />,
        child: [
          { index: true, element: <LegacyBuildingRedirect to="/asset/capitalPlanning" /> },
          {
            path: 'capitalPlanning',
            element: <CapitalPlanning />,
            permissions: ['Analytics', 'AnalyticsDashboards'],
            permissionMode: 'all',
            features: ['CapitalPlanning'],
          },
          {
            path: 'lease',
            element: <LeaseAbstraction />,
            permissions: ['Analytics', 'AnalyticsDashboards'],
            permissionMode: 'all',
            features: ['LeaseIntelligence'],
          },
          {
            path: 'roof',
            element: <RoofAbstraction />,
            permissions: ['Analytics', 'AnalyticsDashboards'],
            permissionMode: 'all',
            features: ['RoofIntelligence'],
          },
          {
            path: 'compliance',
            element: <ComplianceRecordsBuilding />,
            permissions: ['Analytics', 'AnalyticsDashboards'],
            permissionMode: 'all',
            features: ['ComplianceIntelligence'],
          },
        ],
      },
      {
        // One nav item, two tabs (nav reorg): the Insurance Dashboard is the
        // landing page (spec §2), COI Information renders through the
        // buildingInfo shell aliased to the same tab list.
        path: 'coi',
        element: <MenuTabs type="coi" key="coi" />,
        child: [
          { index: true, element: <LegacyBuildingRedirect to="/coi/insuranceDashboard" /> },
          { path: 'insuranceDashboard', element: <InsuranceDashboard />, permissions: ['InsuranceDashboard'] },
        ],
      },
      {
        // One nav item, three tabs (nav reorg): Alerts / Engine / Subscriptions,
        // each behind its own guard. Engine is the default landing (spec §2) —
        // the sidebar links straight to it; the index covers bare /notifications.
        path: 'notifications',
        element: <MenuTabs type="notifications" key="notifications" />,
        child: [
          { index: true, element: <LegacyBuildingRedirect to="/notifications/engine" /> },
          { path: 'alerts', element: <Notifications />, permissions: ['AnalyticsAlerts'] },
          { path: 'engine', element: <NotificationEngine /> },
          { path: 'subscriptions', element: <SubscriptionList />, permissions: ['AnalyticsSubscriptions'] },
          // Activate staff only, matching the backend's SecuredMethods guard on
          // Notification/GetEmailLog.
          { path: 'emailLog', element: <EmailLog />, permissions: ['GlobalManageCustomers'] },
        ],
      },
      // Old standalone paths — now tabs; links keep resolving.
      { path: 'alerts', element: <LegacyBuildingRedirect to="/notifications/alerts" /> },
      { path: 'reports', element: <LegacyBuildingRedirect to="/dataArchive/reports" /> },
      { path: 'subscriptions', element: <LegacyBuildingRedirect to="/notifications/subscriptions" /> },
      {
        // One nav item, four tabs (nav reorg): the shell is open to anyone who
        // can see at least one tab, each tab carries its own guard. Legal needs
        // a Resource wrapper for its react-admin <List> context — same pattern
        // as DataArchive's own default export.
        path: 'dataArchive',
        element: <MenuTabs type="dataArchive" key="dataArchive" />,
        child: [
          { index: true, element: <DataArchive />, permissions: ['ManageDateArchive'] },
          {
            path: 'legal',
            element: <Resource name="legal" key="legalArchive" list={Legal} />,
            permissions: ['Legal'],
          },
          { path: 'reports', element: <Reports />, permissions: ['AnalyticsReports'] },
          { path: 'migration', element: <Migration />, permissions: ['ManageDateArchive'] },
          { path: 'impakMigration', element: <ImpakMigration />, permissions: ['ManageDateArchive'] },
        ],
      },
      { path: 'visitorLog', element: <VisitorLog /> },
      { path: 'notificationEngine', element: <LegacyBuildingRedirect to="/notifications/engine" /> },
      // Building-scoped dashboards that live outside /analytics; scoping them
      // keeps the sidebar's building context when a report escapes the tabs.
      // They render outside the analytics shell, so each mounts the shared
      // category header itself — same title row and tab strip the shell draws,
      // which is what makes a tab hop across the boundary look like a no-op.
      {
        path: 'workOrderDashboard',
        element: (
          <>
            <AnalyticsCategoryHeader standalone hideTitle />
            <WorkOrderDashboard />
          </>
        ),
        permissions: ['WorkOrderDashboard'],
      },
      {
        path: 'workOrderIntelligence',
        element: (
          <>
            <AnalyticsCategoryHeader standalone />
            <WorkOrderIntelligenceDashboard />
          </>
        ),
        permissions: ['WorkOrderDashboard'],
        features: ['WorkOrderIntelligence'],
      },
      {
        path: 'accountingDashboard',
        element: (
          <>
            <AnalyticsCategoryHeader standalone hideTitle />
            <AccountingDashboard />
          </>
        ),
      },
      {
        path: 'masterNotepadDashboard',
        element: (
          <>
            <AnalyticsCategoryHeader standalone />
            <MasterNotepadDashboard />
          </>
        ),
        permissions: ['MasterNotepad'],
      },
      {
        disabled: true,
        path: 'collectionDashboard',
        element: (
          <>
            <AnalyticsCategoryHeader standalone />
            <CollectionDashboard />
          </>
        ),
      },
      {
        path: 'deficiencyTracker',
        element: (
          <>
            <AnalyticsCategoryHeader standalone hideTitle />
            <DeficiencyTracker />
          </>
        ),
      },
      { path: 'deficiencyDetail', element: <DeficiencyDetail /> },
      { path: 'customFeedbackDetail', element: <CustomFeedbackDetail /> },
    ],
  },
  {
    path: '/admin/roles',
    element: <RolesList />,
    permissions: ['GlobalManageRoles'],
  },
  {
    path: '/admin/roles/:id',
    element: <RolesEdit />,
    permissions: ['GlobalManageRoles'],
  },
  {
    path: '/admin/featureFlags',
    element: <FeatureFlagsPage />,
    permissions: ['GlobalManageFeatureFlags'],
  },
  {
    path: '/admin/systemSettings',
    element: <SystemSettingsPage />,
    permissions: ['GlobalManageSystemSettings'],
  },
  {
    path: '/qrcode/:groupId/:bId/:qrId?',
    element: <QRCodeEdit />,
  },
  {
    // Create mode only — editing an account lives at /accountCreate/:gId/:section.
    path: '/accountCreate',
    noLayout: false,
    element: <AccountCreate />,
    permissions: ['ManageGroups', 'PortfolioTeam', 'GlobalManageCustomers'],
  },
  {
    path: '/accountCreate/:gId',
    noLayout: false,
    element: <AccountInfoSection />,
    permissions: ['ManageGroups', 'PortfolioTeam', 'GlobalManageCustomers'],
    child: [{ path: ':section', element: <AccountCreate /> }],
  },
  {
    path: 'collectSurvey',
    element: <CollectHome />,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    // Survey 2.0 recipient page — emailed tokenized link, no login
    path: '/collect/s/:token',
    element: <CollectSurveyPage />,
    noLayout: true,
  },
  {
    path: 'systemJobs',
    element: <SystemJobs />,
    permissions: ['GlobalManageCustomers'],
  },
  {
    path: 'telemetry',
    element: <Telemetry />,
    permissions: ['GlobalManageCustomers'],
  },
  {
    // DEV-ONLY (3191 IQ cache viewer) — unlinked dev route, no nav/menu entry
    path: 'equipmentIQCache',
    element: <EquipmentIQCache />,
  },
  {
    path: 'intelligencePortal',
    element: <IntelligencePortal />,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: 'customerActivity',
    element: <CustomerActivity />,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    // Every customer's tickets (specs#3553): Activate staff only, like System Jobs.
    path: 'ticketMonitor',
    element: <TicketMonitor />,
    permissions: ['GlobalManageCustomers'],
  },
  {
    path: 'verifySurvey/:gId?/:bId?',
    element: <VerifySurvey />,
  },
  {
    path: 'contentCollection',
    element: <ContentCollection />,
  },
  // Pre-/p/:gId portfolio URLs (bookmarks, emailed links, un-migrated
  // navigate() calls): rebuilt onto the selected portfolio by the shim.
  { path: 'portfolioTeam', element: <LegacyPortfolioRedirect to="/portfolioTeam" /> },
  { path: 'pcaHub', element: <LegacyPortfolioRedirect to="/pcaHub" /> },
  { path: '/portfolioAnalytics/*', element: <LegacyPortfolioRedirect to="/portfolioAnalytics" /> },
  {
    // Portfolio scope: every portfolio-page URL carries the portfolio id, the
    // way /b/:bId carries the building, so links are shareable. Children are
    // relative; PortfolioScope syncs redux from :gId (switching the session for
    // a member of several portfolios, or the selection for AcAdmin) and
    // remounts the page on a switch (specs#3464).
    path: '/p/:gId',
    element: <PortfolioScope />,
    child: [
      {
        path: 'portfolioTeam',
        element: <PortfolioTeam />,
        permissions: ['ManageGroups', 'PortfolioTeam', 'GlobalManageCustomers'],
      },
      {
        // PCA Hub (specs#3296): the report template is portfolio-level, so the
        // page follows the selected portfolio rather than a building.
        path: 'pcaHub',
        element: <PcaHubHome />,
        permissions: ['ManagePcaHub'],
        features: ['PcaHub'],
      },
      {
        path: 'portfolioAnalytics',
        // Mirrors the building analytics passthrough: the category tab strip renders
        // inside the dashboard shell and the sidebar lists the categories, so the old
        // Dashboard / Subscriptions MenuTabs shell is gone.
        element: <Outlet />,
        permissions: ['ViewAnalytics', 'PortfolioTeam'],
        child: [
          { index: true, element: <Navigate to="globalDashboard" replace /> },
          {
            path: 'globalDashboard',
            element: <Dashboard key="/globalDashboard" />,
            child: [
              {
                path: 'globalCleaningIQ',
                element: <CleaningIQPortfolio />,
              },
              {
                path: 'globalEngineeringIQ',
                element: <EngineeringIQPortfolio />,
              },
              {
                path: 'globalSecurityIQ',
                element: <SecurityIQPortfolio />,
              },
              {
                path: 'globalReport/:bId?',
                element: <GlobalReport />,
              },
              {
                path: 'globalInspectionReport',
                element: <InspectionReportGlobal />,
              },
              {
                path: 'globalTenantExperience/:typeIndex?',
                element: <TenantExperience />,
              },
              {
                path: 'globalInsuranceDashboard',
                element: <GlobalInsuranceDashboard />,
              },
              {
                path: 'globalLeaseAbstraction',
                element: <GlobalLeaseAbstraction />,
              },
              {
                path: 'globalRoofAbstraction',
                element: <GlobalRoofAbstraction />,
              },
              {
                path: 'globalComplianceRecords',
                element: <GlobalComplianceRecords />,
              },
              {
                path: 'globalDataIntegrityDashboard',
                element: <PortfolioDataIntegrityDashboard />,
              },
              {
                path: 'globalPropertyCharacteristicsIndex',
                element: <PropertyCharacteristicsIndex />,
              },
              {
                path: 'globalGoogleRatingReport',
                element: <GoogleRatingReport type="portfolioAnalytics" />,
              },
              {
                disabled: true,
                path: 'globalUpsellMatrix',
                element: <UpsellMatrixReport />,
              },
              {
                path: 'globalPortfolioRoundAnalytics/:type',
                element: <PortfolioRoundAnalytics />,
              },
              {
                disabled: true,
                path: 'globalMobileEmployee',
                element: <PortfolioMobileEmployee />,
              },
              {
                path: 'globalJobReport',
                element: <JobReport />,
              },
              {
                disabled: true,
                path: 'globalResidentFeedback',
                element: <ResidentFeedback />,
              },
              {
                path: 'globalTeamPerformance',
                element: <GlobalTeamPerformance />,
              },
              {
                path: 'globalCapitalPlanning',
                element: <GlobalCapitalPlanning />,
              },
              {
                path: 'globalPMCompliance',
                element: <GlobalPMCompliance />,
              },
              {
                path: 'globalEquipmentDashboard',
                element: <GlobalEquipmentDashboard />,
              },
            ],
          },
          {
            path: 'subscriptions',
            element: <SubscriptionList />,
          },
        ],
      },
      PORTFOLIO_DASHBOARD_ROUTES.workOrder,
      PORTFOLIO_DASHBOARD_ROUTES.accounting,
      PORTFOLIO_DASHBOARD_ROUTES.collection,
      PORTFOLIO_DASHBOARD_ROUTES.deficiency,
    ],
  },
  { path: '/contacts/*', element: <LegacyBuildingRedirect to="/contacts" /> },
  { path: '/library/*', element: <LegacyBuildingRedirect to="/library" /> },
  { path: '/analytics/*', element: <LegacyBuildingRedirect to="/analytics" /> },
  {
    path: '/analyticsFeedback/:bId',
    element: <Feedback />,
  },
  {
    path: '/surveyWelcome/:nextPathname?/:bId?/:builderId?',
    element: <SurveyWelcome />,
  },
  {
    path: '/digitalProof',
    element: <DigitalProofWelcome />,
    noLayout: true,
  },
  {
    path: '/signProof',
    element: <SignProof />,
    noLayout: true,
  },
  {
    path: '/digitalProofCarousel',
    element: <DigitalProofCarousel />,
    noLayout: true,
  },
  {
    path: '/editPMLibrary/:woId',
    element: <EditPMLibrary />,
  },
  {
    path: 'subLeadCapture/:type/:eventId',
    element: <SubLeadCapture />,
  },
  {
    path: '/updateCOI/:vendorId',
    element: <UpdateCOI />,
    noLayout: true,
  },
  {
    path: '/updateCOI/:rfId/:type',
    element: <UpdateCOI />,
    noLayout: true,
  },
  {
    path: '/reviewCOI/:coiId',
    element: <ReviewCOI />,
    noLayout: true,
  },
  {
    path: '/collection/review',
    element: <CollectionReport />,
    noLayout: true,
  },
  {
    path: '/collection/report',
    element: <CollectionReportSubmit />,
    noLayout: true,
  },
  {
    path: '/twoQREdit',
    element: <TwoQREdit />,
  },
  {
    path: '/twoQREdit/:pId',
    element: <TwoQREdit />,
  },
  {
    path: '/pdf/:roundType/:gId/:ptId/:bId/:startDate/:endDate/:title',
    element: <PdfRoundAnalytics />,
    noLayout: true,
  },
  {
    path: '/pdfRoundDetail/:iptId',
    element: <PdfRoundDetail />,
    noLayout: true,
  },
  {
    // Work Order Intelligence (3310). Filters travel as a query string rather than as path
    // segments: they are optional, variable in number, and several are comma-joined lists,
    // none of which a fixed path pattern handles without empty placeholder segments.
    path: '/pdfWorkOrderIntelligence',
    element: <PdfWorkOrderIntelligence />,
    noLayout: true,
  },
  {
    // Tenant Feedback Intelligence (specs#3488). Same shape as the WOI route above: the
    // scope rides in the query string (gId, bIds, groupName) and the render service
    // reaches the page without preview or embed flags.
    path: '/pdfTenantFeedback',
    element: <PdfTenantFeedback />,
    noLayout: true,
  },
  {
    // PM Intelligence (specs#3509). Same shape as the two routes above: the scope (gId,
    // bId, groupName) and the chosen sections ride in the query string, and the render
    // service reaches the page without preview or embed flags.
    path: '/pdfPMIntelligence',
    element: <PdfPMIntelligence />,
    noLayout: true,
  },
  {
    path: '/pdfTeamPerformance/:gId?/:bId?/:startDate?/:endDate?',
    element: <PdfTeamPerformance />,
    noLayout: true,
  },
  {
    path: '/pdfGoogleReviewReport/:rId',
    element: <PdfGoogleReviewReport />,
    noLayout: true,
  },
  {
    path: '/pdfSocialReputation/:rId',
    element: <PdfSocialReputation />,
    noLayout: true,
  },
  {
    path: '/pdfIncidentReport/:id',
    element: <PdfIncidentReport />,
    noLayout: true,
  },
  {
    path: '/pdfProofReport/:gId/:bId/:type',
    element: <PdfProofReport />,
    noLayout: true,
  },
  { path: '/marketing/*', element: <LegacyBuildingRedirect to="/marketing" /> },
  {
    path: '/masterLibrary',
    element: <MenuTabs type="masterLibrary" key="masterLibrary" />,
    permissions: ['ActivateCustomerFeedback'],
    child: [
      {
        path: '/masterLibrary/printLibrary',
        element: <PrintLibrary />,
      },
      {
        path: '/masterLibrary/printHubLibrary',
        element: <PrintHubLibrary />,
      },
      {
        path: '/masterLibrary/modelHubLibrary',
        element: <ModelHubLibrary />,
      },
      {
        path: '/masterLibrary/printQRLibrary',
        element: <PrintQRLibrary />,
      },
      {
        path: '/masterLibrary/programLibrary',
        element: <ProgramLibrary />,
      },
    ],
  },
  {
    path: '/scans',
    element: <MenuTabs type="scans" key="scans" />,
    permissions: ['ActivateCustomerFeedback'],
    child: [
      {
        path: '/scans/qrLocation',
        element: <ScanReport key="Location" />,
      },
      {
        path: '/scans/qrName',
        element: <ScanReport type="QrName" key="QrName" />,
      },
      {
        path: '/scans/pinActivity',
        element: <PinActivity />,
      },
    ],
  },
  {
    path: '/googleRatingReport',
    element: <GoogleRatingReport type="Activate" />,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: '/activateNotepad',
    element: <ActivateNotepad />,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: '/surveyLibrary',
    element: <MenuTabs type="surveyLibrary" key="surveyLibrary" />,
    permissions: ['ActivateCustomerFeedback'],
    child: [
      {
        path: '/surveyLibrary/surveyBuilder',
        element: <SurveyBuilder />,
      },
      {
        path: '/surveyLibrary/surveyBuilder/:gId/:bId/:builderId?',
        element: <SurveyBuilder />,
      },
      {
        path: '/surveyLibrary/surveyList',
        element: <SurveyList />,
      },
    ],
  },
  {
    path: '/reportBuilder',
    element: <MenuTabs type="reportBuilder" key="reportBuilder" />,
    permissions: ['ActivateCustomerFeedback'],
    child: [
      {
        path: '/reportBuilder/builder',
        element: <ReportBuilder />,
      },
      {
        path: '/reportBuilder/subscriptions',
        element: <ReportBuilderSubscriptions />,
      },
    ],
  },
  { path: '/dataArchive/*', element: <LegacyBuildingRedirect to="/dataArchive" /> },
  { path: '/notifications/*', element: <LegacyBuildingRedirect to="/notifications" /> },
  { path: '/asset/*', element: <LegacyBuildingRedirect to="/asset" /> },
  { path: '/coi/*', element: <LegacyBuildingRedirect to="/coi" /> },
  {
    path: '/pdfReportBuilder/:rId',
    element: <PdfReportBuilder />,
    noLayout: true,
  },
  {
    path: '/PdfBuilderSolution/:sId',
    element: <PdfBuilderSolution />,
    noLayout: true,
  },
  {
    path: 'weeklyexception',
    element: <PdfWeeklyException />,
    noLayout: true,
  },
  {
    path: '/pdfLegal/:id',
    element: <PdfLegal />,
    noLayout: true,
  },
  {
    path: '/pdfCharacteristics/:gId/:bId',
    element: <PdfCharacteristics />,
    noLayout: true,
  },
  {
    path: '/pdfMechanicalSystem/:id',
    element: <PdfMechanicalSystem />,
    noLayout: true,
  },
  {
    path: '/pdfChiefEngineer/:rId',
    element: <PdfChiefEngineer />,
    noLayout: true,
  },
  {
    path: '/pdfWorkLetter/:rId',
    element: <PdfWorkLetter />,
    noLayout: true,
  },
  {
    path: '/pdfMobileEmployee/:gId/:bId?/:startDate?/:endDate?',
    element: <PdfMobileEmployee />,
    noLayout: true,
  },
  {
    path: '/feedbackEngine',
    element: <FeedbackEngine />,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: '/researchTest',
    element: <ResearchTest />,
    noLayout: true,
  },
  {
    path: '/video',
    element: <Video />,
    noLayout: true,
  },
  {
    path: '/pdfInstallationGuide/:rId',
    element: <PdfInstallationGuide />,
    noLayout: true,
  },
  {
    path: '/promptEngineering',
    element: <PromptEngineering />,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: '/lookbook',
    element: <Lookbook />,
    permissions: ['ActivateCustomerFeedback'],
  },
  { path: '/customFeedbackDetail/:bId', element: <LegacyBuildingRedirect to="/customFeedbackDetail" /> },
  {
    path: '/multipleContentSurvey/:id',
    element: <MultipleContentSurvey />,
  },
  { path: '/workOrderDashboard', element: <LegacyBuildingRedirect to="/workOrderDashboard" /> },
  { path: '/workOrderIntelligence', element: <LegacyBuildingRedirect to="/workOrderIntelligence" /> },
  { path: '/workOrderDashboardGlobal', element: <LegacyPortfolioRedirect to="/workOrderDashboardGlobal" /> },
  { path: '/accountingDashboard', element: <LegacyBuildingRedirect to="/accountingDashboard" /> },
  { path: '/accountingDashboardGlobal', element: <LegacyPortfolioRedirect to="/accountingDashboardGlobal" /> },
  { path: '/masterNotepadDashboard', element: <LegacyBuildingRedirect to="/masterNotepadDashboard" /> },
  { path: '/collectionDashboard', element: <LegacyBuildingRedirect to="/collectionDashboard" /> },
  { disabled: true, path: '/collectionDashboardGlobal', element: <LegacyPortfolioRedirect to="/collectionDashboardGlobal" /> },
  {
    path: '/pdfBuildingInvoice/:gId/:bId/:startDate/:endDate/:fId',
    element: <PdfBuildingInvoice />,
    noLayout: true,
  },
  {
    path: '/pdfGroupInvoice/:gId/:startDate/:endDate/:fId',
    element: <PdfGroupInvoice />,
    noLayout: true,
  },
  {
    path: '/pdfDetailInvoice',
    element: <PdfDetailInvoice />,
    noLayout: true,
  },
  {
    path: '/pdfTenantInvoice/:gId/:bId/:startDate/:endDate/:fId',
    element: <PdfTenantInvoice />,
    noLayout: true,
  },
  { path: '/calendar', element: <LegacyBuildingRedirect to="/calendar" /> },
  { path: '/visitorLog', element: <LegacyBuildingRedirect to="/visitorLog" /> },
  { path: '/notificationEngine', element: <LegacyBuildingRedirect to="/notificationEngine" /> },
  // The sidebar falls back to unscoped paths when no building is selected
  // (Menu.tsx), and these had no unscoped route at all — the links 404ed
  // instead of landing on the building-scoped page. /subscriptions resolved to
  // the react-admin Resource list, which skipped the AnalyticsSubscriptions
  // guard its scoped twin enforces.
  { path: '/alerts', element: <LegacyBuildingRedirect to="/alerts" /> },
  { path: '/reports', element: <LegacyBuildingRedirect to="/reports" /> },
  { path: '/subscriptions', element: <LegacyBuildingRedirect to="/subscriptions" /> },
  { path: '/buildingInfo/*', element: <LegacyBuildingRedirect to="/buildingInfo" /> },
  // Pre-rework paths, still live in bookmarks and old report emails. The
  // /analytics/* shim already catches them, but only by scoping them into the
  // scoped rename shims — two redirects where one will do.
  { path: '/analytics/notifications', element: <LegacyBuildingRedirect to="/alerts" /> },
  { path: '/analytics/reports', element: <LegacyBuildingRedirect to="/reports" /> },
  { path: '/analytics/subscription', element: <LegacyBuildingRedirect to="/subscriptions" /> },
  {
    path: '/impersonation',
    element: <UserImpersonation />,
    noLayout: false,
    permissions: ['GlobalManageCustomers'],
  },
  { path: '/deficiencyTracker', element: <LegacyBuildingRedirect to="/deficiencyTracker" /> },
  { path: '/deficiencyTrackerGlobal', element: <LegacyPortfolioRedirect to="/deficiencyTrackerGlobal" /> },
  { path: '/deficiencyDetail/:bId', element: <LegacyBuildingRedirect to="/deficiencyDetail" /> },
  {
    path: '/pdfCustomerFeedbackOnSR/:gId/:bId',
    element: <PdfFeedbackOnSR />,
    noLayout: true,
  },
]

export const resourceRouter = [
  {
    path: 'installationRound',
    Element: InstallationRound,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: 'digitalProofReport',
    Element: QrEngineeringDashboard,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: 'printProof',
    Element: PrintProof,
    permissions: ['ActivateCustomerFeedback'],
  },
  {
    path: 'legal',
    Element: Legal,
    permissions: ['Legal'],
  },
]

export default Router
